'use client';

import {
  Avatar,
  Button,
  Checkbox,
  type ColumnDef,
  createColumnHelper,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  IconButton,
  Switch,
  Tooltipß,
} from '@otter-ai/den-components';
import {
  ChevronDownIcon,
  DotsHorizontalIcon,
  FileUserIcon,
  InformationIcon,
} from '@otter-ai/den-icons';
import type { GetWorkspaceResponse } from '@otter-ai/recall-client';
import { useAtom } from 'jotai';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { memo, useMemo } from 'react';
import { PATH_URL } from '@/constants/path-constants';
import { DAY_MS } from '@/constants/time';
import { selectedMemberIdsAtom } from '@/features/manage-team/team-profile/atoms';
import type { TeamMembersActions } from '@/features/manage-team/team-profile/useTeamMembersActions';
import { useGetCurrentUser } from '@/shared/hooks/current-user/useGetCurrentUser';
import { useFormatRelativeTime } from '@/shared/hooks/time-format/useFormatRelativeTime';
import { trackTrialUsageLinkClick } from './tracking';

type Member = GetWorkspaceResponse['workspace']['members'][number];
type TableRow = {
  name: string;
  avatarUrl: string | null;
  email: string;
  memberEmail: string;
  memberId: number | null;
  /** The workspace member relationship id (for actions) */
  relationshipId: number;
  workspaceId: number;
  isAdmin: boolean;
  isOwner: boolean;
  isPending: boolean;
  isDeactivated: boolean;
  isHidden: boolean;
  roleOrderIndex: number;
  notetaker: boolean;
  // Status-related fields
  memberStatus: string;
  licenseStatus: string | null;
  isSelfJoin: boolean;
  creatorEmail: string | null;
  lastActivityTime: string | null;
  isTrialExtended: boolean;
  trueUpDate: string | null;
  isDirectorySynced: boolean;
};

export type TeamMembersViewMode = 'members' | 'requested' | 'trials';

type TeamMembersTableProps = {
  members: Member[];
  ownerEmail: string | null;
  /** Whether the current user is an admin of the workspace */
  isCurrentUserAdmin: boolean;
  /** Whether the current user is the owner of the workspace */
  isCurrentUserOwner: boolean;
  /** Callback when toggling notetaker setting */
  onNotetakerToggleAction?: (memberId: number, enabled: boolean) => void;
  /** All member action handlers */
  memberActions: TeamMembersActions;
  // Bulk management
  /** Whether bulk management is enabled */
  enableBulkManagement?: boolean;
  /** Set of selected relationship IDs */
  selectedIds?: Set<number>;
  /** Callback when selection changes */
  onSelectionChangeAction?: (selectedIds: Set<number>) => void;
  /** Whether the user can hide/unhide members (enterprise/edu/experiment) */
  canHideMembers?: boolean;
  /** Current view mode for the table (affects which columns are shown) */
  viewMode?: TeamMembersViewMode;
  /** Whether admins can control virtual assistant (notetaker) settings */
  allowAdminVaControl?: boolean;
  /** Whether the workspace is a basic (free) workspace - only basic workspace members can leave */
  isBasicWorkspace?: boolean;
  /** Whether the user can access the analytics tab (renders last active as link) */
  canAccessAnalytics?: boolean;
};

/**
 * Determines the display role for a member.
 * Owner > Admin > Member
 */
function getDisplayRole(
  isOwner: boolean,
  isAdmin: boolean,
  t: ReturnType<typeof useTranslations<'manage-team:team-profile'>>,
): string {
  if (isOwner) {
    return t('table.roles.owner');
  }
  if (isAdmin) {
    return t('table.roles.admin');
  }
  return t('table.roles.member');
}

/**
 * Returns a sort index for the role.
 * Owner (-2) > Admin (-1) > Member (0)
 */
function getRoleOrderIndex(
  memberEmail: string,
  isAdmin: boolean,
  ownerEmail: string | null,
): number {
  if (ownerEmail && memberEmail.toLowerCase() === ownerEmail.toLowerCase()) {
    return -2; // Owner first
  }
  if (isAdmin) {
    return -1; // Admin second
  }
  return 0; // Member last
}

/**
 * Returns a sort index for status.
 * Active (0) > Invited (1) > Deactivated (2)
 */
function getStatusOrderIndex(member: Member): number {
  if (member.deactivated_at) {
    return 2; // Deactivated last
  }
  if (member.is_pending || member.member_status === MEMBER_STATUS.INVITED) {
    return 1; // Invited/pending after active
  }
  return 0; // Active members first
}

/**
 * Determines if the role dropdown should be shown for a member.
 */
function canShowRoleDropdown(
  row: TableRow,
  isCurrentUserAdmin: boolean,
): boolean {
  if (row.isOwner) return false;
  if (row.isPending) return false;
  if (row.isDeactivated) return false;
  return isCurrentUserAdmin;
}

type RoleCellProps = {
  row: TableRow;
  isCurrentUserAdmin: boolean;
  isCurrentUserOwner: boolean;
  memberActions: TeamMembersActions;
  t: ReturnType<typeof useTranslations<'manage-team:team-profile'>>;
};

/**
 * Cell component for the Role column with dropdown functionality.
 */
function RoleCell({
  row,
  isCurrentUserAdmin,
  isCurrentUserOwner,
  memberActions,
  t,
}: RoleCellProps) {
  const displayRole = getDisplayRole(row.isOwner, row.isAdmin, t);
  const showDropdown = canShowRoleDropdown(row, isCurrentUserAdmin);

  if (!showDropdown || row.memberId === null) {
    return <span className="font-semibold text-sm ml-2">{displayRole}</span>;
  }

  const handlePromoteToOwner = () => {
    if (row.memberId !== null) {
      memberActions.promoteToOwner(row.memberId);
    }
  };

  const handleDemoteToMember = () => {
    if (row.memberId !== null) {
      memberActions.demoteToMember(row.memberId);
    }
  };

  const handlePromoteToAdmin = () => {
    if (row.memberId !== null) {
      memberActions.promoteToAdmin(row.memberId);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outlineNeutral"
          version="legacy"
          size="md"
          className="flex items-center gap-1 -ml-2"
        >
          {displayRole}
          <ChevronDownIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align="start">
          {row.isAdmin ? (
            <>
              {isCurrentUserOwner && (
                <DropdownMenuItem onClick={handlePromoteToOwner}>
                  {t('table.roleActions.promoteToOwner')}
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={handleDemoteToMember}>
                {t('table.roleActions.demoteToMember')}
              </DropdownMenuItem>
            </>
          ) : (
            <DropdownMenuItem onClick={handlePromoteToAdmin}>
              {t('table.roleActions.promoteToAdmin')}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}

const SIX_MONTHS_MS = 180 * DAY_MS;

// Status constants matching the API values
export const MEMBER_STATUS = {
  DEACTIVATED: 'deactivated',
  DELETED: 'deleted',
  INVITED: 'invited pending acceptance',
  JOINED: 'joined team',
  REJECTED: 'rejected',
  REQUESTED: 'requested to join',
} as const;

type StatusCellProps = {
  memberStatus: string;
  licenseStatus: string | null;
  isOwner: boolean;
  isSelfJoin: boolean;
  creatorEmail: string | null;
  isDeactivated: boolean;
  isHidden: boolean;
  isTrialExtended: boolean;
  isDirectorySynced: boolean;
  t: ReturnType<typeof useTranslations<'manage-team:team-profile'>>;
};

/**
 * Determines the display status text for a member.
 */
function getStatusText(
  memberStatus: string,
  licenseStatus: string | null,
  isDeactivated: boolean,
  isHidden: boolean,
  t: ReturnType<typeof useTranslations<'manage-team:team-profile'>>,
): string {
  let status: string;
  if (isDeactivated || memberStatus === MEMBER_STATUS.DEACTIVATED) {
    status = t('table.statuses.deactivated');
  } else if (memberStatus === MEMBER_STATUS.REQUESTED) {
    status = t('table.statuses.requested');
  } else if (memberStatus === MEMBER_STATUS.INVITED) {
    status = t('table.statuses.invited');
  } else if (licenseStatus === 'trial') {
    status = t('table.statuses.trial');
  } else {
    status = t('table.statuses.joined');
  }
  return isHidden ? t('table.statuses.hidden', { status }) : status;
}

/**
 * Determines the tooltip detail for a member's status.
 * Returns null if no tooltip should be shown.
 */
function getStatusDetail(
  isOwner: boolean,
  isSelfJoin: boolean,
  creatorEmail: string | null,
  isDirectorySynced: boolean,
  memberStatus: string,
  t: ReturnType<typeof useTranslations<'manage-team:team-profile'>>,
): string | null {
  if (isOwner) {
    return t('table.statusDetails.createdWorkspace');
  }
  if (isDirectorySynced && memberStatus === MEMBER_STATUS.JOINED) {
    return t('table.statusDetails.viaWorkspaceDiscovery');
  }
  if (isSelfJoin) {
    return t('table.statusDetails.viaWorkspaceDiscovery');
  }
  if (creatorEmail) {
    return t('table.statusDetails.invitedBy', { email: creatorEmail });
  }
  return null;
}

/**
 * Cell component for the Status column with optional tooltip.
 */
function StatusCell({
  memberStatus,
  licenseStatus,
  isOwner,
  isSelfJoin,
  creatorEmail,
  isDeactivated,
  isHidden,
  isTrialExtended,
  isDirectorySynced,
  t,
}: StatusCellProps) {
  const statusText = getStatusText(
    memberStatus,
    licenseStatus,
    isDeactivated,
    isHidden,
    t,
  );
  const statusDetail = getStatusDetail(
    isOwner,
    isSelfJoin,
    creatorEmail,
    isDirectorySynced,
    memberStatus,
    t,
  );

  return (
    <div className="flex items-center gap-1">
      <span>{statusText}</span>
      {statusDetail && (
        <Tooltipß caption={statusDetail} delayDuration={0}>
          <button type="button" className="text-subtle hover:text-default">
            <InformationIcon size={16} />
          </button>
        </Tooltipß>
      )}
      {isTrialExtended && (
        <span className="rounded bg-surface-tertiary px-1.5 py-0.5 text-xs text-subtle">
          {t('table.statuses.extended')}
        </span>
      )}
    </div>
  );
}

type ActionCellProps = {
  row: TableRow;
  memberActions: TeamMembersActions;
  canHideMembers: boolean;
  /** Whether the current user is an admin of the workspace */
  isCurrentUserAdmin: boolean;
  /** Whether the current user is the owner of the workspace */
  isCurrentUserOwner: boolean;
  /** The current user's email (lowercase) for identifying their own row */
  currentUserEmail: string | null;
  /** Whether the workspace is a basic (free) workspace */
  isBasicWorkspace: boolean;
  t: ReturnType<typeof useTranslations<'manage-team:team-profile'>>;
};

type MenuItemConfig = {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'negative';
  show?: boolean;
};

/**
 * Reusable three-dot menu component for member actions.
 * Filters out items where show is false and renders nothing if no items remain.
 */
function MemberActionsMenu({
  items,
  ariaLabel,
}: {
  items: MenuItemConfig[];
  ariaLabel: string;
}) {
  const visibleItems = items.filter((item) => item.show !== false);

  if (visibleItems.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton variant="ghost" size="md" aria-label={ariaLabel}>
          <DotsHorizontalIcon size={32} />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuPortal>
        <DropdownMenuContent align="end">
          {visibleItems.map((item) => (
            <DropdownMenuItem
              key={item.label}
              onClick={item.onClick}
              className={item.variant === 'negative' ? 'text-negative' : ''}
            >
              {item.label}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenuPortal>
    </DropdownMenu>
  );
}

/**
 * Cell component for the Actions column.
 * Shows different actions based on member status:
 * - Requested: Approve/Decline buttons
 * - Pending/Invited: Resend button + 3-dot menu with Cancel invitation
 * - Deactivated: 3-dot menu with Reactivate and Delete
 * - Joined: 3-dot menu with Revoke access + Hide/Unhide
 * - Current user (basic member): Leave workspace option
 */
function ActionCell({
  row,
  memberActions,
  canHideMembers,
  isCurrentUserAdmin,
  isCurrentUserOwner,
  currentUserEmail,
  isBasicWorkspace,
  t,
}: ActionCellProps) {
  const isRequested = row.memberStatus === MEMBER_STATUS.REQUESTED;
  const isInvited = row.memberStatus === MEMBER_STATUS.INVITED || row.isPending;
  const isDeactivated =
    row.isDeactivated || row.memberStatus === MEMBER_STATUS.DEACTIVATED;
  const isTrial = row.licenseStatus === 'trial';
  const canHide = canHideMembers && !row.isHidden && !isDeactivated;
  const canUnhide = canHideMembers && row.isHidden && !isDeactivated;

  // Check if this is the current user's own row and they're a basic member (not admin/owner) in a basic workspace
  const isOwnRow =
    currentUserEmail !== null &&
    row.memberEmail.toLowerCase() === currentUserEmail;
  const isBasicMember = !isCurrentUserAdmin && !isCurrentUserOwner;
  const canLeaveWorkspace = isOwnRow && isBasicMember && isBasicWorkspace;

  const memberInfo = {
    relationshipId: row.relationshipId,
    userId: row.memberId ?? 0,
    name: row.name,
    email: row.email,
  };

  // For basic members viewing their own row: show Leave workspace option
  if (canLeaveWorkspace) {
    return (
      <div className="pl-[69px]">
        <MemberActionsMenu
          ariaLabel={t('table.moreOptions')}
          items={[
            {
              label: t('actions.leaveWorkspace'),
              onClick: () => memberActions.leaveWorkspace(),
              variant: 'negative',
            },
          ]}
        />
      </div>
    );
  }

  // Non-admin users cannot perform actions on other members
  if (isBasicMember) {
    return null;
  }

  // For requested members: show Approve/Decline buttons
  if (isRequested) {
    return (
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="outlinePrimary"
          version="legacy"
          onClick={() => memberActions.approveRequest(row.memberId ?? 0)}
        >
          {t('actions.approve')}
        </Button>
        <Button
          size="sm"
          variant="outlineNeutral"
          version="legacy"
          onClick={() => memberActions.declineRequest(row.memberId ?? 0)}
        >
          {t('actions.decline')}
        </Button>
      </div>
    );
  }

  const ariaLabel = t('table.moreOptions');

  // For invited/pending members: show Resend button + 3-dot menu
  if (isInvited) {
    return (
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="primary"
          onClick={() => memberActions.resendInvitation(row.memberEmail)}
        >
          {t('actions.resend')}
        </Button>
        <MemberActionsMenu
          ariaLabel={ariaLabel}
          items={[
            {
              label: t('actions.cancelInvitation'),
              onClick: () => memberActions.cancelInvitation(memberInfo),
              variant: 'negative',
            },
          ]}
        />
      </div>
    );
  }

  // For deactivated members: show 3-dot menu with Reactivate and Delete
  if (isDeactivated) {
    return (
      <div className="pl-[69px]">
        <MemberActionsMenu
          ariaLabel={ariaLabel}
          items={[
            {
              label: t('actions.reactivateMember'),
              onClick: () => memberActions.reactivate(memberInfo),
            },
            {
              label: t('actions.deleteMember'),
              onClick: () => memberActions.deleteMember(memberInfo),
              variant: 'negative',
            },
          ]}
        />
      </div>
    );
  }

  // For owner or admin: only show hide/unhide if available
  if (row.isOwner || row.isAdmin) {
    return (
      <div className="pl-[69px]">
        <MemberActionsMenu
          ariaLabel={ariaLabel}
          items={[
            {
              label: t('actions.hideMember'),
              onClick: () => memberActions.hide(memberInfo),
              show: canHide,
            },
            {
              label: t('actions.unhideMember'),
              onClick: () => memberActions.unhide(memberInfo),
              show: canUnhide,
            },
          ]}
        />
      </div>
    );
  }

  // For trial members: show 3-dot menu with Cancel trial
  if (isTrial) {
    return (
      <div className="pl-[69px]">
        <MemberActionsMenu
          ariaLabel={ariaLabel}
          items={[
            {
              label: t('actions.cancelTrial'),
              onClick: () => memberActions.cancelTrial(memberInfo),
              variant: 'negative',
            },
          ]}
        />
      </div>
    );
  }

  // For regular joined members: show 3-dot menu with Hide/Unhide and Revoke access
  return (
    <div className="pl-[69px]">
      <MemberActionsMenu
        ariaLabel={ariaLabel}
        items={[
          {
            label: t('actions.hideMember'),
            onClick: () => memberActions.hide(memberInfo),
            show: canHide,
          },
          {
            label: t('actions.unhideMember'),
            onClick: () => memberActions.unhide(memberInfo),
            show: canUnhide,
          },
          {
            label: t('actions.revokeAccessToWorkspace'),
            onClick: () => memberActions.revokeAccess(memberInfo),
            variant: 'negative',
          },
        ]}
      />
    </div>
  );
}

/**
 * Memoized cell component for the Name column to prevent avatar flashing on re-renders.
 */
const NameCell = memo(function NameCell({
  name,
  avatarUrl,
  isCurrentUser,
  currentUserSuffix,
  isDirectorySynced,
  t,
}: {
  name: string;
  avatarUrl: string | null;
  isCurrentUser: boolean;
  currentUserSuffix: string;
  isDirectorySynced: boolean;
  t: ReturnType<typeof useTranslations<'manage-team:team-profile'>>;
}) {
  return (
    <div className="flex items-center gap-3">
      <Avatar name={name} imageUrl={avatarUrl} size="sm" hideTooltip />
      <div className="flex items-center gap-2">
        <span className="font-semibold text-default">
          {name}
          {isCurrentUser && ` ${currentUserSuffix}`}
        </span>
        {isDirectorySynced && (
          <Tooltipß
            caption={t('table.nameColumn.syncedWithDirectory')}
            delayDuration={0}
          >
            <button type="button" className="text-subtle hover:text-default">
              <FileUserIcon size={20} />
            </button>
          </Tooltipß>
        )}
      </div>
    </div>
  );
});

type NotetakerCellProps = {
  enabled: boolean;
  memberId: number | null;
  onToggle?: (memberId: number, enabled: boolean) => void;
  isInvited: boolean;
  t: ReturnType<typeof useTranslations<'manage-team:team-profile'>>;
};

/**
 * Cell component for the Notetaker column.
 * No local state - relies on optimistic cache updates for instant feedback.
 * Returns null for invited members (they don't have notetaker settings yet).
 */
function NotetakerCell({
  enabled,
  memberId,
  onToggle,
  isInvited,
  t,
}: NotetakerCellProps) {
  // Don't show toggle for invited members
  if (isInvited) {
    return null;
  }

  const handleToggle = (checked: boolean) => {
    if (memberId !== null) {
      onToggle?.(memberId, checked);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Switch checked={enabled} onCheckedChange={handleToggle} />
      <span className="text-subtle">
        {enabled
          ? t('table.notetakerToggle.on')
          : t('table.notetakerToggle.off')}
      </span>
    </div>
  );
}

const columnHelper = createColumnHelper<TableRow>();

/**
 * Formats the last activity time as relative time, with a 6-month cutoff.
 * Returns dash if no activity or activity is older than 6 months.
 * When showing activity, renders as a link to the usage-report page only if user can access analytics.
 */
function ActiveWithinCell({
  lastActivityTime,
  memberEmail,
  canAccessAnalytics,
}: {
  lastActivityTime: string | null;
  memberEmail: string;
  canAccessAnalytics: boolean;
}) {
  const formatRelativeTime = useFormatRelativeTime();

  if (!lastActivityTime) {
    return <span className="text-subtle">-</span>;
  }

  const lastActivityDate = new Date(lastActivityTime);

  // Check for invalid date
  if (Number.isNaN(lastActivityDate.getTime())) {
    return <span className="text-subtle">-</span>;
  }

  const now = new Date();
  const elapsed = now.getTime() - lastActivityDate.getTime();

  // If older than 6 months, show dash
  if (elapsed >= SIX_MONTHS_MS) {
    return <span className="text-subtle">-</span>;
  }

  const relativeTime = formatRelativeTime(lastActivityDate);

  // Only render as a link if user can access analytics tab
  if (canAccessAnalytics) {
    return (
      <Link
        href={`${PATH_URL.TEAM_USAGE_REPORT}?member_email=${encodeURIComponent(memberEmail)}`}
        className="text-primary hover:underline"
        onClick={() => trackTrialUsageLinkClick(relativeTime)}
      >
        {relativeTime}
      </Link>
    );
  }

  return <span className="text-subtle">{relativeTime}</span>;
}

export function TeamMembersTable({
  members,
  ownerEmail,
  isCurrentUserAdmin,
  isCurrentUserOwner,
  onNotetakerToggleAction,
  memberActions,
  enableBulkManagement = false,
  selectedIds = new Set(),
  onSelectionChangeAction,
  canHideMembers = false,
  viewMode = 'members',
  allowAdminVaControl = false,
  isBasicWorkspace = false,
  canAccessAnalytics = false,
}: TeamMembersTableProps) {
  const t = useTranslations('manage-team:team-profile');
  const { user: currentUser } = useGetCurrentUser();
  const currentUserEmail = currentUser?.email?.toLowerCase() ?? null;
  const isRequestedView = viewMode === 'requested';
  const isTrialsView = viewMode === 'trials';
  const isMembersView = viewMode === 'members';
  const tableRows = useMemo(
    () =>
      members
        .sort((a, b) => {
          // First sort by role (Owner > Admin > Member)
          const roleOrder =
            getRoleOrderIndex(a.member_email, Boolean(a.is_admin), ownerEmail) -
            getRoleOrderIndex(b.member_email, Boolean(b.is_admin), ownerEmail);
          if (roleOrder !== 0) return roleOrder;
          // Then sort by status (Active > Invited > Deactivated)
          return getStatusOrderIndex(a) - getStatusOrderIndex(b);
        })
        .map((member) => {
          const baseRow = convertMemberToTableRow(member, ownerEmail);
          return {
            ...baseRow,
            roleOrderIndex: getRoleOrderIndex(
              baseRow.memberEmail,
              baseRow.isAdmin,
              ownerEmail,
            ),
          };
        }),
    [members, ownerEmail],
  );

  // Selection helpers
  const allSelected =
    tableRows.length > 0 &&
    tableRows.every((row) => selectedIds.has(row.relationshipId));
  const someSelected =
    tableRows.some((row) => selectedIds.has(row.relationshipId)) &&
    !allSelected;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const newSelected = new Set(tableRows.map((row) => row.relationshipId));
      onSelectionChangeAction?.(newSelected);
    } else {
      onSelectionChangeAction?.(new Set());
    }
  };

  const handleSelectRow = (relationshipId: number, checked: boolean) => {
    const newSelected = new Set(selectedIds);
    if (checked) {
      newSelected.add(relationshipId);
    } else {
      newSelected.delete(relationshipId);
    }
    onSelectionChangeAction?.(newSelected);
  };

  const columns = useMemo(
    () =>
      [
        // Checkbox column (only when bulk management is enabled and in members view)
        ...(enableBulkManagement && isMembersView
          ? [
              columnHelper.display({
                id: 'select',
                cell: (info) => (
                  <TableCheckboxCell
                    relationshipId={info.row.original.relationshipId}
                  />
                ),
                enableSorting: false,
              }),
            ]
          : []),
        columnHelper.accessor('name', {
          header: t('table.columns.name'),
          cell: (info) => (
            <NameCell
              name={info.getValue() || t('table.unknownName')}
              avatarUrl={info.row.original.avatarUrl}
              isCurrentUser={
                currentUserEmail !== null &&
                info.row.original.memberEmail.toLowerCase() === currentUserEmail
              }
              currentUserSuffix={t('table.currentUserSuffix')}
              isDirectorySynced={info.row.original.isDirectorySynced}
              t={t}
            />
          ),
        }),
        columnHelper.accessor('email', {
          header: t('table.columns.email'),
          cell: (info) => (
            <span className="text-subtle">{info.getValue()}</span>
          ),
        }),
        // Role column (only shown in members view)
        ...(isMembersView
          ? [
              columnHelper.accessor('roleOrderIndex', {
                id: 'role',
                header: t('table.columns.role'),
                cell: (info) => (
                  <RoleCell
                    row={info.row.original}
                    isCurrentUserAdmin={isCurrentUserAdmin}
                    isCurrentUserOwner={isCurrentUserOwner}
                    memberActions={memberActions}
                    t={t}
                  />
                ),
                sortingFn: 'basic',
              }),
            ]
          : []),
        // Notetaker column (shown in members and trials view when allowAdminVaControl is enabled and user is admin/owner)
        ...((isMembersView || isTrialsView) &&
        allowAdminVaControl &&
        (isCurrentUserAdmin || isCurrentUserOwner)
          ? [
              columnHelper.accessor('notetaker', {
                header: t('table.columns.notetaker'),
                cell: (info) => {
                  const row = info.row.original;
                  const isInvited =
                    row.memberStatus === MEMBER_STATUS.INVITED || row.isPending;
                  return (
                    <NotetakerCell
                      enabled={info.getValue()}
                      memberId={row.memberId}
                      onToggle={onNotetakerToggleAction}
                      isInvited={isInvited}
                      t={t}
                    />
                  );
                },
                sortingFn: 'basic',
              }),
            ]
          : []),
        // Status column (only shown for admins/owners)
        ...(isCurrentUserAdmin || isCurrentUserOwner
          ? [
              columnHelper.accessor('memberStatus', {
                id: 'status',
                header: t('table.columns.status'),
                cell: (info) => (
                  <StatusCell
                    memberStatus={info.getValue()}
                    licenseStatus={info.row.original.licenseStatus}
                    isOwner={info.row.original.isOwner}
                    isSelfJoin={info.row.original.isSelfJoin}
                    creatorEmail={info.row.original.creatorEmail}
                    isDeactivated={info.row.original.isDeactivated}
                    isHidden={info.row.original.isHidden}
                    isTrialExtended={info.row.original.isTrialExtended}
                    isDirectorySynced={info.row.original.isDirectorySynced}
                    t={t}
                  />
                ),
                sortingFn: (rowA, rowB) => {
                  // Sort by status order: Active (0) > Invited (1) > Deactivated (2)
                  const getStatusOrder = (row: typeof rowA) => {
                    if (row.original.isDeactivated) return 2;
                    if (
                      row.original.isPending ||
                      row.original.memberStatus === MEMBER_STATUS.INVITED
                    )
                      return 1;
                    return 0;
                  };
                  return getStatusOrder(rowA) - getStatusOrder(rowB);
                },
              }),
            ]
          : []),
        // Invited by column (only shown in requested view)
        ...(isRequestedView
          ? [
              columnHelper.accessor('creatorEmail', {
                id: 'invitedBy',
                header: t('table.columns.invitedBy'),
                cell: (info) => {
                  const row = info.row.original;
                  if (row.isSelfJoin) {
                    return (
                      <span className="text-subtle">
                        {t('table.statusDetails.viaWorkspaceDiscovery')}
                      </span>
                    );
                  }
                  const creatorEmail = info.getValue();
                  if (creatorEmail) {
                    return <span className="text-subtle">{creatorEmail}</span>;
                  }
                  return <span className="text-subtle">-</span>;
                },
                enableSorting: false,
              }),
            ]
          : []),
        // Active within column (shown in members and trials views, only for admins/owners)
        ...((isCurrentUserAdmin || isCurrentUserOwner) &&
        (isMembersView || isTrialsView)
          ? [
              columnHelper.accessor('lastActivityTime', {
                header: () => (
                  <div className="flex items-center gap-1">
                    <span>{t('table.columns.activeWithin')}</span>
                    <Tooltipß
                      caption={t('table.activeWithinTooltip')}
                      delayDuration={0}
                    >
                      <button
                        type="button"
                        className="text-subtle hover:text-default"
                      >
                        <InformationIcon size={16} />
                      </button>
                    </Tooltipß>
                  </div>
                ),
                cell: (info) => (
                  <ActiveWithinCell
                    lastActivityTime={info.getValue()}
                    memberEmail={info.row.original.memberEmail}
                    canAccessAnalytics={canAccessAnalytics}
                  />
                ),
                sortingFn: (rowA, rowB) => {
                  const timeA = rowA.original.lastActivityTime;
                  const timeB = rowB.original.lastActivityTime;

                  // Handle null values - put them at the end
                  if (!timeA && !timeB) return 0;
                  if (!timeA) return 1;
                  if (!timeB) return -1;

                  // Sort by date (most recent first by default)
                  const dateA = new Date(timeA).getTime();
                  const dateB = new Date(timeB).getTime();

                  return dateB - dateA;
                },
              }),
            ]
          : []),
        // Billing starts column (only shown in trials view for admins/owners)
        ...((isCurrentUserAdmin || isCurrentUserOwner) && isTrialsView
          ? [
              columnHelper.accessor('trueUpDate', {
                id: 'billingStarts',
                header: t('table.columns.billingStarts'),
                cell: (info) => {
                  const trueUpDate = info.getValue();
                  if (!trueUpDate) {
                    return <span className="text-subtle">-</span>;
                  }
                  const date = new Date(trueUpDate);
                  if (Number.isNaN(date.getTime())) {
                    return <span className="text-subtle">-</span>;
                  }

                  // Check if trial is ending soon (within 10 days)
                  const now = Date.now();
                  const timeUntilBilling = date.getTime() - now;
                  const TEN_DAYS_MS = 10 * DAY_MS;
                  const isTrialEnding =
                    timeUntilBilling > 0 && timeUntilBilling < TEN_DAYS_MS;

                  return (
                    <div className="flex items-center gap-1.5">
                      {isTrialEnding && (
                        <span className="h-2 w-2 rounded-full bg-negative" />
                      )}
                      <span className="text-subtle">
                        {date.toLocaleDateString('en', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                      </span>
                    </div>
                  );
                },
                enableSorting: false,
              }),
            ]
          : []),
        columnHelper.display({
          id: 'actions',
          header: '',
          cell: (info) => (
            <ActionCell
              row={info.row.original}
              memberActions={memberActions}
              canHideMembers={canHideMembers}
              isCurrentUserAdmin={isCurrentUserAdmin}
              isCurrentUserOwner={isCurrentUserOwner}
              currentUserEmail={currentUserEmail}
              isBasicWorkspace={isBasicWorkspace}
              t={t}
            />
          ),
          enableSorting: false,
        }),
      ] as ColumnDef<TableRow>[],
    [
      t,
      isCurrentUserAdmin,
      isCurrentUserOwner,
      onNotetakerToggleAction,
      memberActions,
      enableBulkManagement,
      selectedIds,
      allSelected,
      someSelected,
      handleSelectAll,
      handleSelectRow,
      tableRows,
      canHideMembers,
      currentUserEmail,
      isRequestedView,
      isTrialsView,
      isMembersView,
      allowAdminVaControl,
      isBasicWorkspace,
    ],
  );

  if (tableRows.length === 0) {
    if (isRequestedView) {
      return (
        <div className="flex items-center justify-center py-12 text-subtle">
          {t('emptyState.noRequests')}
        </div>
      );
    }
    if (isTrialsView) {
      return (
        <div className="flex items-center justify-center py-12 text-subtle">
          {t('emptyState.noTrials')}
        </div>
      );
    }
  }

  return (
    <DataTable<TableRow>
      columns={columns}
      data={tableRows}
      rowClassName={(row) => (row.isDeactivated ? 'bg-state-disabled' : '')}
      floating
      data-testid="team-members-table"
    />
  );
}

function convertMemberToTableRow(
  member: Member,
  ownerEmail: string | null,
): Omit<TableRow, 'roleOrderIndex'> {
  // last_activity_time is not in recall-client types yet
  const lastActivityTime = (member as { last_activity_time?: string })
    .last_activity_time;
  // is_trial_extended is not in recall-client types yet
  const isTrialExtended = Boolean(
    (member as { is_trial_extended?: boolean }).is_trial_extended,
  );

  const isOwner = Boolean(
    ownerEmail &&
      member.member_email.toLowerCase() === ownerEmail.toLowerCase(),
  );

  // Creator can be null or an empty object {} from the API
  // We need to check if it's a proper object with id and email properties
  const creator =
    member.creator &&
    typeof member.creator === 'object' &&
    'id' in member.creator &&
    'email' in member.creator
      ? (member.creator as { id: number; email: string })
      : null;

  // Check if member self-joined (member.id === creator.id)
  const isSelfJoin = Boolean(
    member.member_id && creator && member.member_id === creator.id,
  );

  const isDirectorySynced = Boolean(member.is_directory_synced);

  return {
    name: member.member.name || '',
    avatarUrl: (member.member.avatar_url as string | null) ?? null,
    email: (member.member.email || member.member_email || '') as string,
    memberEmail: member.member_email,
    memberId: member.member_id,
    relationshipId: member.id as number,
    workspaceId: member.workspace_id as number,
    isAdmin: Boolean(member.is_admin),
    isOwner,
    isPending: Boolean(member.is_pending),
    isDeactivated: Boolean(member.deactivated_at),
    isHidden: Boolean((member as { is_hidden?: boolean }).is_hidden),
    notetaker: (member.allow_virtual_assistant ?? false) as boolean,
    // Status-related fields
    memberStatus: member.member_status as string,
    licenseStatus: (member.license_status as string) ?? null,
    isSelfJoin,
    creatorEmail: creator?.email ?? null,
    lastActivityTime: lastActivityTime ?? null,
    isTrialExtended,
    trueUpDate: (member.true_up_date as string) ?? null,
    isDirectorySynced,
  };
}

function TableCheckboxCell({ relationshipId }: { relationshipId: number }) {
  const [selectedIds, setSelectedIds] = useAtom(selectedMemberIdsAtom);
  return (
    <Checkbox
      checked={selectedIds.has(relationshipId)}
      onCheckedChange={(checked) =>
        setSelectedIds((prev) => {
          const newSet = new Set(prev);
          if (checked) {
            newSet.add(relationshipId);
          } else {
            newSet.delete(relationshipId);
          }
          return newSet;
        })
      }
    />
  );
}
